import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Zap, Menu, X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import type { Session } from "@supabase/supabase-js";
import { cn } from "@/lib/utils";

export function PublicHeader() {
    const [session, setSession] = useState<Session | null>(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session);
        });

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session);
        });

        return () => subscription.unsubscribe();
    }, []);

    // Close menu when location changes
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setIsMenuOpen(false);
    }, [location.pathname]);

    return (
        <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200/50 backdrop-blur-md font-sans">
            <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2 group">
                    <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white shadow-lg shadow-blue-200 group-hover:scale-110 transition-transform">
                        <Zap className="w-5 h-5 fill-current" />
                    </div>
                    <span className="font-bold text-xl tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-600">
                        Adaptive Endurance
                    </span>
                </Link>

                {/* Desktop Nav */}
                <nav className="hidden md:flex items-center gap-8">
                    <NavLink to="/features" current={location.pathname === "/features"}>Features</NavLink>
                    <NavLink to="/roadmap" current={location.pathname === "/roadmap"}>Roadmap</NavLink>
                    <NavLink to="/pricing" current={location.pathname === "/pricing"}>Pricing</NavLink>
                    <NavLink to="/about" current={location.pathname === "/about"}>About</NavLink>
                </nav>

                <div className="flex items-center gap-2 sm:gap-4">
                    <div className="hidden sm:flex items-center gap-2">
                        <Link to={session ? "/dashboard" : "/login"}>
                            <Button variant="ghost" className="text-slate-600 hover:bg-slate-50 px-3 sm:px-4 text-xs font-bold uppercase tracking-widest">
                                {session ? "Dashboard" : "Sign In"}
                            </Button>
                        </Link>
                    </div>
                    <Link to="/onboarding">
                        <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4 sm:px-6 shadow-lg shadow-blue-100 transition-all hover:scale-105 active:scale-95 text-xs font-bold uppercase tracking-widest">
                            Start Training
                        </Button>
                    </Link>

                    {/* Mobile Menu Toggle */}
                    <button
                        className="md:hidden p-2 text-slate-600 hover:bg-slate-50 rounded-lg"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                    >
                        {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile Nav Overlay */}
            {isMenuOpen && (
                <div className="md:hidden absolute top-16 left-0 right-0 bg-white border-b border-slate-200 shadow-xl p-4 flex flex-col gap-4 animate-in slide-in-from-top-4 duration-200">
                    <MobileNavLink to="/features">Features</MobileNavLink>
                    <MobileNavLink to="/roadmap">Roadmap</MobileNavLink>
                    <MobileNavLink to="/pricing">Pricing</MobileNavLink>
                    <MobileNavLink to="/about">About Us</MobileNavLink>
                    <hr className="border-slate-100" />
                    <Link to={session ? "/dashboard" : "/login"} className="w-full">
                        <Button variant="outline" className="w-full justify-center text-xs font-bold uppercase tracking-widest">
                            {session ? "Dashboard" : "Sign In"}
                        </Button>
                    </Link>
                </div>
            )}
        </header>
    );
}

function NavLink({ to, children, current }: { to: string; children: React.ReactNode; current: boolean }) {
    return (
        <Link
            to={to}
            className={cn(
                "text-xs font-bold uppercase tracking-widest transition-colors",
                current ? "text-blue-600" : "text-slate-500 hover:text-blue-600"
            )}
        >
            {children}
        </Link>
    );
}

function MobileNavLink({ to, children }: { to: string; children: React.ReactNode }) {
    return (
        <Link
            to={to}
            className="text-sm font-bold uppercase tracking-widest text-slate-600 hover:text-blue-600 py-2"
        >
            {children}
        </Link>
    );
}
